import { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@context/AuthContext';
import { useTwilioDevice } from '@hooks/useTwilioDevice';
import { useCallState } from '@hooks/useCallState';
import { hangupAll, toggleMute, sendDigits } from '@services/twilioService';
import { sendToVoicemail, hangUpCall } from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import TabNavigation from '@components/TabNavigation/TabNavigation';
import ActiveCallBar from '@components/CallControls/ActiveCallBar';
import PhoneControlsTab from '@components/Dashboard/tabs/PhoneControlsTab';
import CallSearchTab from '@components/Dashboard/tabs/CallSearchTab';
import ErrorsTab from '@components/Dashboard/tabs/ErrorsTab';
import VoiceTemplatesTab from '@components/Dashboard/tabs/VoiceTemplatesTab';
import IvrQuestionsTab from '@components/Dashboard/tabs/IvrQuestionsTab';
import IvrFlowTab from '@components/Dashboard/tabs/IvrFlowTab';
import './Dashboard.css';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { device, isReady, isLoading, error } = useTwilioDevice();
  const {
    activeCall,
    callStatus,
    isMuted,
    setIsMuted,
    callDuration,
    callConnection,
    setCallConnection,
    startOutgoingCall,
    endCall,
  } = useCallState();
  const [activeTab, setActiveTab] = useState('phone');
  const [selectedQueue, setSelectedQueue] = useState('');
  const [callingNumber, setCallingNumber] = useState('');
  const [sendingVoicemail, setSendingVoicemail] = useState(false);
  const [callError, setCallError] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCallStarted = useCallback((phoneNumber) => {
    setCallingNumber(phoneNumber);
    startOutgoingCall(phoneNumber);
  }, [startOutgoingCall]);

  const handleCallConnectionReady = useCallback((connection) => {
    setCallConnection(connection);
  }, [setCallConnection]);

  const handleCallEnded = useCallback(() => {
    setCallingNumber('');
    setSendingVoicemail(false);
    setCallError('');
    endCall();
  }, [endCall]);

  const handleToggleMute = useCallback(() => {
    if (!callConnection) return;
    const newMuted = toggleMute(callConnection);
    setIsMuted(newMuted);
  }, [callConnection, setIsMuted]);

  const handleSendDigits = useCallback((digits) => {
    if (!callConnection) return;
    sendDigits(callConnection, digits);
  }, [callConnection]);

  const handleHangup = useCallback(() => {
    // Disconnect the Twilio SDK call immediately so the UI clears without waiting for the network.
    try {
      hangupAll();
    } catch (err) {
      console.error('[Dashboard] SDK hangup failed:', err);
    }

    // Notify the backend to terminate via Twilio REST API and record the end time.
    // Fire-and-forget — we don't block the UI on this.
    const callSid = callConnection?.parameters?.CallSid;
    if (callSid) {
      hangUpCall(callSid).catch((err) => {
        console.error('[Dashboard] Backend hang-up failed (call already ended or network error):', err);
      });
    }

    handleCallEnded();
  }, [callConnection, handleCallEnded]);

  const handleSendToVoicemail = useCallback(async () => {
    if (!callConnection) {
      setCallError('No active call to send to voicemail');
      return;
    }

    try {
      setSendingVoicemail(true);
      setCallError('');

      const callSid = callConnection.parameters?.CallSid;
      if (!callSid) {
        setCallError('Cannot determine call SID');
        setSendingVoicemail(false);
        return;
      }

      await sendToVoicemail(callSid);

      hangupAll();
      handleCallEnded();
    } catch (err) {
      setCallError('Failed to send voicemail: ' + parseApiError(err));
      setSendingVoicemail(false);
    }
  }, [callConnection, handleCallEnded]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'phone':
        return (
          <PhoneControlsTab
            device={device}
            isReady={isReady}
            isLoading={isLoading}
            error={error}
            activeCall={activeCall}
            callStatus={callStatus}
            onCallStarted={handleCallStarted}
            onCallEnded={handleCallEnded}
            onCallConnectionReady={handleCallConnectionReady}
            selectedQueue={selectedQueue}
            onQueueSelected={setSelectedQueue}
            defaultTestPatientGuid={user?.defaultTestPatientGuid}
          />
        );
      case 'call-search':
        return <CallSearchTab />;
      case 'errors':
        return <ErrorsTab />;
      case 'voicemail':
        return <VoiceTemplatesTab />;
      case 'ivr-questions':
        return <IvrQuestionsTab />;
      case 'ivr-flow':
        return <IvrFlowTab />;
      default:
        return (
          <PhoneControlsTab
            device={device}
            isReady={isReady}
            isLoading={isLoading}
            error={error}
            activeCall={activeCall}
            callStatus={callStatus}
            onCallStarted={handleCallStarted}
            onCallEnded={handleCallEnded}
            onCallConnectionReady={handleCallConnectionReady}
            selectedQueue={selectedQueue}
            onQueueSelected={setSelectedQueue}
            defaultTestPatientGuid={user?.defaultTestPatientGuid}
          />
        );
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>S3 Phone System</h1>
        <div className="header-right">
          <div className="device-status">
            {isLoading && <span className="status-badge loading">Initializing...</span>}
            {isReady && (
              <span className="status-badge ready">
                <span className="status-dot"></span> Ready
              </span>
            )}
            {error && <span className="status-badge error">Error: {error.message || 'Unknown'}</span>}
            {!isLoading && !isReady && !error && <span className="status-badge loading">Waiting...</span>}
          </div>
          <div className="user-menu">
            {user && <span className="user-name">{user.email}</span>}
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      {callStatus === 'active' && (
        <ActiveCallBar
          phoneNumber={callingNumber}
          callDuration={callDuration}
          isMuted={isMuted}
          onToggleMute={handleToggleMute}
          onSendDigits={handleSendDigits}
          onSendToVoicemail={handleSendToVoicemail}
          sendingVoicemail={sendingVoicemail}
          onHangup={handleHangup}
        />
      )}

      {callError && (
        <div className="active-call-bar-error">{callError}</div>
      )}

      <div className="dashboard-container">
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
        <main className="dashboard-content">
          {renderTabContent()}
        </main>
      </div>

      {/* Debug Info */}
      <div className="debug-info">
        <details>
          <summary>Device Debug Info</summary>
          <div className="debug-content">
            <p><strong>Device Ready:</strong> {isReady ? 'Yes' : 'No'}</p>
            <p><strong>Device Loading:</strong> {isLoading ? 'Yes' : 'No'}</p>
            <p><strong>Device Object:</strong> {device ? 'Initialized' : 'Not initialized'}</p>
            <p><strong>Error:</strong> {error ? error.message || JSON.stringify(error) : 'None'}</p>
          </div>
        </details>
      </div>

      <footer className="dashboard-footer">
        <p>S3 Phone System Dashboard v1.0.0</p>
      </footer>
    </div>
  );
}
